import { inferRouteObject } from 'react-router-typed-object';

import { CampaignStatus } from '@/services/ws-digital/src/main/resources/rest-api/types';
import { generatePath, Navigate } from '@/js/libs/router';
import { PrivateRoute, Roles, and, not, or } from '@/js/libs/use-auth';

import { PositionsType } from 'src/components/PositionsStageFilter/index.tsx';
import type { Condition } from 'src/components/RoleBasedRedirector.tsx';
import RoleBasedRedirector from 'src/components/RoleBasedRedirector.tsx';
import RouteParamsValidator from 'src/components/RouteParamsValidator.tsx';
import { createCampaignTypeValidator } from 'src/scenes/Campaign/helpers.ts';
import Campaign from 'src/scenes/Campaign/index.ts';
import { BroadcastRoleChecker } from 'src/scenes/Orders/containers/BroadcastPositions/components/BroadcastRoleChecker.tsx';
import { validateBroadcastType } from 'src/scenes/Orders/containers/BroadcastPositions/helpers.ts';
import { campaignsListRouterSearchParamsSchema } from 'src/scenes/Orders/containers/Campaigns/pages/CampaignsList/constants.ts';
import Orders from 'src/scenes/Orders/index.ts';
import Status from 'src/scenes/Status/index.ts';

const index = true;

const ordersConditions: Condition[] = [
  { roles: Roles.POSTNETZ_BRANCH_USER, to: generatePath('/orders/booking') },
  { roles: Roles.READ_ONLY_POSTNETZ_BRANCH_USER, to: generatePath('/orders/booking') },
  { roles: Roles.POSTADMANAGER, to: generatePath('/orders/post-campaigns') },
  { roles: Roles.YALEO_SELF_BOOKING_USER, to: generatePath('/self-booking/campaigns') },
  { roles: Roles.AGENCY, to: generatePath('/orders/campaigns') },
];

export const orders1RouteObject = inferRouteObject({
  path: 'orders',
  Component: Orders.Helmet,
  children: [
    {
      index,
      element: <RoleBasedRedirector conditions={ordersConditions} noAccessElement={<Status.NotFound />} />,
    },
    {
      path: PositionsType.DIGITAL,
      element: (
        <PrivateRoute
          roles={and(
            or(Roles.MANAGE_DIGITAL_PRODUCTS, Roles.BOOK_CAMPAIGNS, Roles.MANAGE_ACCOUNTS, Roles.VIEW_CAMPAIGNS),
            not(or(Roles.POSTNETZ_BRANCH_USER, Roles.READ_ONLY_POSTNETZ_BRANCH_USER)),
          )}
          noAccessComponent={Status.NotFound}
        />
      ),
      children: [{ index, Component: Orders.DigitalPositions }],
    },
    {
      path: PositionsType.PRINT,
      element: (
        <PrivateRoute
          roles={and(Roles.MANAGE_PRINT_PRODUCTS, not(or(Roles.POSTNETZ_BRANCH_USER, Roles.READ_ONLY_POSTNETZ_BRANCH_USER)))}
          noAccessComponent={Status.NotFound}
        />
      ),
      children: [{ index, Component: Orders.PrintPositions }],
    },
    {
      path: ':broadcastType',
      element: <RouteParamsValidator validate={validateBroadcastType} invalidStateElement={<Status.NotFound />} />,
      children: [
        {
          Component: BroadcastRoleChecker,
          children: [{ index, Component: Orders.BroadcastPositions }],
        },
      ],
    },
    {
      path: PositionsType.POS,
      element: (
        <PrivateRoute
          roles={and(Roles.MANAGE_POS_PRODUCTS, not(or(Roles.POSTNETZ_BRANCH_USER, Roles.READ_ONLY_POSTNETZ_BRANCH_USER)))}
          noAccessComponent={Status.NotFound}
        />
      ),
      children: [{ index, element: <Orders.PointOfSalePositions /> }],
    },
    {
      path: 'consulting-positions',
      element: (
        <PrivateRoute roles={and(Roles.MANAGE_CONSULTING_PRODUCTS, not(Roles.POSTNETZ_BRANCH_USER))} noAccessComponent={Status.NotFound} />
      ),
      children: [{ index, element: <Orders.ConsultingPositions /> }],
    },
    {
      path: 'campaigns',
      element: (
        <PrivateRoute
          roles={and(
            or(Roles.ADMIN, Roles.BOOK_CAMPAIGNS, Roles.AGENCY),
            not(Roles.POSTADMANAGER),
            not(Roles.YALEO_SELF_BOOKING_USER),
            not(or(Roles.POSTNETZ_BRANCH_USER, Roles.READ_ONLY_POSTNETZ_BRANCH_USER)),
          )}
          noAccessComponent={Status.NotFound}
        />
      ),
      children: [
        {
          Component: Campaign.Helmet,
          children: [
            {
              element: <PrivateRoute roles={Roles.AGENCY} noAccessComponent={Status.NotFound} />,
              children: [{ index, Component: Orders.Campaigns.CampaignsList }],
            },
            {
              path: ':type',
              children: [
                {
                  index,
                  element: <Navigate to='/orders/campaigns' replace />,
                },
                {
                  element: (
                    <RouteParamsValidator
                      validate={createCampaignTypeValidator(Object.keys(CampaignStatus))}
                      invalidStateElement={<Status.NotFound />}
                    />
                  ),
                },
                {
                  element: (
                    <RouteParamsValidator
                      validate={createCampaignTypeValidator(CampaignStatus.OFFERED)}
                      invalidStateElement={<Status.NotFound />}
                    />
                  ),
                  children: [
                    {
                      element: <PrivateRoute roles={or(Roles.ADMIN, Roles.BOOK_CAMPAIGNS)} noAccessComponent={Status.NotFound} />,
                      children: [
                        {
                          path: 'new-order',
                          children: [
                            { index, Component: Campaign.Edit },
                            { path: ':accountId', Component: Campaign.Edit },
                          ],
                        },
                        {
                          path: 'new-order-alfa',
                          children: [
                            { index, Component: Orders.Campaigns.Order },
                            { path: ':accountId', Component: Orders.Campaigns.Order },
                          ],
                        },
                      ],
                    },
                    {
                      element: <PrivateRoute roles={or(Roles.ADMIN, Roles.BOOK_CAMPAIGNS)} noAccessComponent={Status.NotFound} />,
                      children: [
                        {
                          path: 'new-order-alfa',
                          children: [
                            { index, Component: Orders.Campaigns.Order },
                            { path: ':accountId', Component: Orders.Campaigns.Order },
                          ],
                        },
                      ],
                    },
                    {
                      path: 'tasks/:taskId/new-order',
                      Component: Campaign.Edit,
                    },
                    {
                      path: 'tasks/:taskId/new-order-alfa',
                      Component: Orders.Campaigns.Order,
                    },
                  ],
                },
                {
                  path: ':campaignId',
                  children: [
                    {
                      element: <PrivateRoute roles={or(Roles.ADMIN, Roles.BOOK_CAMPAIGNS)} noAccessComponent={Status.NotFound} />,
                      children: [
                        {
                          Component: Campaign.EditPermissionChecker,
                          children: [
                            { path: 'edit', Component: Campaign.Edit },
                            { path: 'edit-alfa', Component: Orders.Campaigns.Order },
                            { path: 'edit-positions/:positionType?', Component: Campaign.EditPositions },
                            {
                              path: 'edit-positions/:positionType/:hasPositionConflict?',
                              Component: Campaign.EditPositions,
                            },
                            { path: 'preview-pdf', Component: Campaign.DocumentPreview },
                          ],
                        },
                        {
                          path: 'history',
                          Component: Campaign.History,
                        },
                      ],
                    },
                    {
                      element: <PrivateRoute roles={Roles.AGENCY} noAccessComponent={Status.NotFound} />,
                      children: [
                        {
                          Component: Campaign.Overview.Providers,
                          children: [
                            {
                              index,
                              Component: Campaign.Overview.Component,
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
      searchParams: campaignsListRouterSearchParamsSchema.parse,
    },
    {
      path: 'campaigns-v2',
      element: (
        <PrivateRoute
          roles={and(
            or(Roles.ADMIN, Roles.BOOK_CAMPAIGNS, Roles.AGENCY),
            not(Roles.POSTADMANAGER),
            not(Roles.YALEO_SELF_BOOKING_USER),
            not(or(Roles.POSTNETZ_BRANCH_USER, Roles.READ_ONLY_POSTNETZ_BRANCH_USER)),
          )}
          noAccessComponent={Status.NotFound}
        />
      ),
      children: [
        {
          Component: Campaign.Helmet,
          children: [
            {
              path: ':campaignId',
              children: [
                {
                  element: <PrivateRoute roles={Roles.AGENCY} noAccessComponent={Status.NotFound} />,
                  children: [
                    {
                      index,
                      Component: Orders.Campaigns.CampaignOverview,
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});
